<template>
  <div class="phone-input-control">
    <div class="phone-group-mobile">
      <div class="phone-input-group">
        <select
          v-model="internalCountryCode"
          class="country-select"
          :class="{ 'required-field': required, 'error': hasError }"
          :disabled="disabled"
          @change="handleCountryChange"
        >
          <option v-for="country in countries" :key="country.id" :value="country.id">
            {{ country.text }}
          </option>
        </select>
        <span class="phone-separator"></span>
        <input
          v-model="internalPhoneNumber"
          type="tel"
          :placeholder="placeholder || t('phoneInput.placeholder')"
          class="k-textbox phone-input"
          :class="{ 'required-field': required, 'error': hasError }"
          :disabled="disabled"
          @input="handlePhoneInput"
          @blur="handleBlur"
        />
      </div>
      <input
        v-if="showExtension"
        v-model="internalExtension"
        :placeholder="t('phoneInput.extension')"
        class="k-textbox ext-input"
        :disabled="disabled"
        @input="handleExtensionInput"
      />
      <select
        v-if="showPhoneType"
        v-model="internalPhoneType"
        class="k-textbox phone-type-select"
        :class="{ 'required-field': required }"
        :disabled="disabled"
        @change="handlePhoneTypeChange"
      >
        <!-- <option value="" disabled>{{ t('phoneInput.type') }}</option> -->
        <option v-for="phoneType in phoneTypes" :key="phoneType.id" :value="phoneType.id">
          {{ phoneType.text }}
        </option>
      </select>
    </div>
    <div v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { GetCountryCode } from '../stores/SettingsStore'
import { GetCountriesFromLocalStorageOrApi, GetCodesFromLocalStorageOrApi } from '../services/CountryCultureService'
import type { IPhoneOutput } from '../types/CommonTypes'

const { t } = useI18n()

// Types
interface DLLOption {
  text: string
  id: string
}

// Country option type for custom country lists
interface CountryOption {
  text: string
  id: string
}

// Props
const props = withDefaults(defineProps<{
  /** The phone number value */
  modelValue?: IPhoneOutput | null
  /** Whether the field is required */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Placeholder text for the phone input */
  placeholder?: string
  /** Whether to show the extension field */
  showExtension?: boolean
  /** Whether to show the phone type dropdown */
  showPhoneType?: boolean
  /** Custom label for the component */
  label?: string
  /** Whether to validate on blur */
  validateOnBlur?: boolean
  /** Custom list of countries to display. If not provided, fetches from API */
  countryList?: CountryOption[]
}>(), {
  required: false,
  disabled: false,
  showExtension: true,
  showPhoneType: true,
  validateOnBlur: true
})

// Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: IPhoneOutput): void
  (e: 'change', value: IPhoneOutput): void
  (e: 'validation', isValid: boolean, errorMessage: string): void
}>()

// Internal state
const internalCountryCode = ref<string>('')
const internalPhoneNumber = ref<string>('')
const internalExtension = ref<string>('')
const internalPhoneType = ref<string>('')
const countries = ref<DLLOption[]>([])
const phoneTypes = ref<DLLOption[]>([])
const hasError = ref<boolean>(false)
const errorMessage = ref<string>('')
const isInitialized = ref<boolean>(false)

// Initialize component
onMounted(async () => {
  await loadCountries()
  await loadPhoneTypes()
  syncFromModelValue()
  await setDefaultCountry()
  setDefaultPhoneType()
  isInitialized.value = true
})

// Load countries from props, API, or localStorage
async function loadCountries() {
  // If a custom country list was provided, use it
  if (props.countryList && props.countryList.length > 0) {
    countries.value = props.countryList
    return
  }

  // Otherwise fetch from API/localStorage
  try {
    const response = await GetCountriesFromLocalStorageOrApi()
    if (response?.items) {
      countries.value = response.items
        .map(country => ({
          text: t(`phoneInput.country.${country.countryCode}`),
          id: country.countryCode
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    }
  } catch (error) {
    console.error('Error loading countries:', error)
    // Fallback to common European countries
    countries.value = [
      { text: 'AT', id: 'AT' },
      { text: 'DE', id: 'DE' },
      { text: 'FR', id: 'FR' },
      { text: 'US', id: 'US' }
    ]
  }
}

// Load phone types from API/localStorage
async function loadPhoneTypes() {
  const defaultPhoneTypes = [
    { text: t('phoneInput.cell'), id: 'Cell' },
    { text: t('phoneInput.home'), id: 'Home' }
  ];

  phoneTypes.value = defaultPhoneTypes;
}

// Set default country code from settings
async function setDefaultCountry() {
  if (internalCountryCode.value) return

  try {
    const defaultCountry = await GetCountryCode()
    if (defaultCountry) {
      // Only use if it exists in the countries list
      const countryExists = countries.value.some(c => c.id === defaultCountry)
      if (countryExists) {
        internalCountryCode.value = defaultCountry
        return
      }
    }
  } catch (error) {
    console.error('Error getting default country code:', error)
  }

  // Fallback to first country if nothing is set
  if (countries.value.length > 0) {
    internalCountryCode.value = countries.value[0].id
  }
}

// Set default phone type to Cell if not provided
function setDefaultPhoneType() {
  if (internalPhoneType.value) return

  internalPhoneType.value = phoneTypes.value[0].id
}

// Sync internal state from modelValue prop
function syncFromModelValue() {
  if (props.modelValue) {
    if (props.modelValue.countryCode) {
      internalCountryCode.value = props.modelValue.countryCode
    }
    internalPhoneNumber.value = props.modelValue.number || ''
    internalExtension.value = props.modelValue.extension || ''
    const modelType = props.modelValue.codeIdPhoneType || ''
    const matched = phoneTypes.value.find(
      (pt) => pt.id.toLowerCase() === modelType.toLowerCase()
    )
    internalPhoneType.value = matched ? matched.id : modelType
  }
}

// Watch for modelValue changes
watch(() => props.modelValue, (newValue) => {
  if (newValue && isInitialized.value) {
    syncFromModelValue()
  }
}, { deep: true })

// Validation function — strips non-numeric chars then validates with libphonenumber-js
function validatePhoneNumber(): { isValid: boolean; errorMessage: string; formattedNumber: string } {
  if (!internalPhoneNumber.value) {
    if (props.required) {
      return {
        isValid: false,
        errorMessage: t('phoneInput.required'),
        formattedNumber: ''
      }
    }
    return { isValid: true, errorMessage: '', formattedNumber: '' }
  }

  // Strip all non-numeric characters for JAS shipping system compatibility
  const originalValue = internalPhoneNumber.value
  const numericOnly = originalValue.replace(/[^0-9]/g, '')
  if (originalValue !== numericOnly) {
    internalPhoneNumber.value = numericOnly
  }

  if (!numericOnly) {
    return {
      isValid: false,
      errorMessage: t('validators.phoneNumericOnly', { fieldName: t('phoneInput.phoneNumber') }),
      formattedNumber: ''
    }
  }

  // Universal length validation: 3-15 digits for all countries
  const digitCount = numericOnly.length

  if (digitCount < 3 || digitCount > 15) {
    return {
      isValid: false,
      errorMessage: t('validators.phoneLengthInvalid', {
        fieldName: t('phoneInput.phoneNumber'),
        min: 3,
        max: 15
      }),
      formattedNumber: ''
    }
  }

  return {
    isValid: true,
    errorMessage: '',
    formattedNumber: numericOnly
  }
}

// Build and emit the output value
function emitValue() {
  const validation = validatePhoneNumber()

  const output: IPhoneOutput = {
    number: internalPhoneNumber.value,
    countryCode: internalCountryCode.value,
    formattedNumber: validation.formattedNumber || internalPhoneNumber.value,
    codeIdPhoneType: internalPhoneType.value,
    extension: internalExtension.value || null
  }

  emit('update:modelValue', output)
  emit('change', output)
}

// Event handlers
function handleCountryChange() {
  // Re-validate when country changes
  if (internalPhoneNumber.value && props.validateOnBlur) {
    const validation = validatePhoneNumber()
    hasError.value = !validation.isValid
    errorMessage.value = validation.errorMessage
    emit('validation', validation.isValid, validation.errorMessage)
  }
  emitValue()
}

function handlePhoneInput() {
  // Strip all non-digit characters in real-time
  const stripped = internalPhoneNumber.value.replace(/[^0-9]/g, '')
  if (stripped !== internalPhoneNumber.value) {
    internalPhoneNumber.value = stripped
  }

  // Validate on every input to update parent's button state
  const validation = validatePhoneNumber()
  hasError.value = !validation.isValid
  errorMessage.value = validation.errorMessage
  emit('validation', validation.isValid, validation.errorMessage)

  emitValue()
}

function handleExtensionInput() {
  emitValue()
}

function handlePhoneTypeChange() {
  emitValue()
}

function handleBlur() {
  if (props.validateOnBlur && internalPhoneNumber.value) {
    const validation = validatePhoneNumber()
    hasError.value = !validation.isValid
    errorMessage.value = validation.errorMessage
    emit('validation', validation.isValid, validation.errorMessage)

    // Update with formatted number
    if (validation.isValid && validation.formattedNumber) {
      emitValue()
    }
  }
}

// Expose validation method for parent components
defineExpose({
  validate: (): boolean => {
    const validation = validatePhoneNumber()
    hasError.value = !validation.isValid
    errorMessage.value = validation.errorMessage
    emit('validation', validation.isValid, validation.errorMessage)
    return validation.isValid
  },
  reset: () => {
    hasError.value = false
    errorMessage.value = ''
  }
})
</script>

<style lang="scss" scoped>
.phone-input-control {
  width: 100%;
}

.phone-group-mobile {
  display: flex;
  gap: $gap-sm;
  align-items: stretch;
  flex-wrap: wrap;
}

.phone-input-group {
  display: flex;
  flex: 1;
  min-width: 200px;
}

.country-select {
  @include typography-body;
  padding: $spacing-2 $spacing-6 $spacing-2 $spacing-2;
  border: 1px solid var(--color-border);
  border-radius: $input-radius 0 0 $input-radius;
  border-right: none;
  background-color: var(--color-background);
  width: 70px;
  min-width: 70px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right $spacing-1 center;
  background-size: 1em;
  cursor: pointer;
}

.country-select:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
}

.country-select:disabled {
  background-color: var(--color-background-mute);
  cursor: not-allowed;
  opacity: 0.7;
}

.phone-separator {
  width: 1px;
  background-color: var(--color-border);
  align-self: stretch;
}

.phone-input {
  border-radius: 0 $radius-sm $radius-sm 0 !important;
  border-left: none !important;
  flex: 1;
  margin: 0 !important;
  min-width: 0;
}

.phone-input:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
}

.k-textbox {
  @include typography-body;
  padding: $spacing-2 $spacing-3;
  border: 1px solid var(--color-border);
  border-radius: $input-radius;
  width: 100%;
  box-sizing: border-box;
}

.k-textbox:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
}

.k-textbox:disabled {
  background-color: var(--color-background-mute);
  cursor: not-allowed;
  opacity: 0.7;
}

.k-textbox.ext-input {
  width: 80px !important;
  min-width: 80px;
  margin: 0;
}

.phone-type-select {
  width: 120px !important;
  min-width: 120px;
  flex: 0 0 130px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right $spacing-2 center;
  background-size: 1em;
  padding-right: $spacing-5;
  cursor: pointer;
}

.required-field {
  border-color: #7c7c7c;
}

.required-field::placeholder {
  color: #7c7c7c;
}

.error {
  border-color: #dc3545 !important;
}

.error:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25) !important;
}

.error-message {
  color: #dc3545;
  @include typography-caption;
  margin-top: $spacing-1;
  font-style: italic;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .phone-group-mobile {
    flex-direction: column;
    gap: $spacing-3;
  }

  .phone-input-group {
    width: 100%;
    min-width: 100%;
  }

  .ext-input {
    width: 100% !important;
    flex: 1 1 100%;
  }

  .phone-type-select {
    width: 100% !important;
    flex: 1 1 100%;
  }
}
</style>
